/**
 * Conversion Intelligence Service
 * Detects existing tracking, maps conversions, and optimizes setup
 */

import { openaiService } from './openaiService';

export interface ConversionEvent {
  name: string;
  pageUrl: string;
  fullUrl: string;
  selector: string;
  triggerType: 'click' | 'submit' | 'pageview';
  value?: number;
  currency?: string;
  confidence: number;
  evidence: string;
}

export interface ConversionTracking {
  platform: 'GTM' | 'GA4' | 'Google Ads' | 'Facebook' | 'Multiple' | 'None';
  trackingIds: {
    gtm?: string;
    ga4?: string;
    googleAds?: string;
    facebookPixel?: string;
  };
  events: ConversionEvent[];
  existingConversions: {
    name: string;
    conversionId?: string;
    action: string;
  }[];
  conversionFunnel: string[];
  primaryConversion?: string;
  recommendedBidding: string;
}

export const conversionIntelligence = {
  /**
   * Analyze website's tracking setup and conversions
   */
  async analyzeConversionTracking(
    url: string,
    htmlContent?: string
  ): Promise<ConversionTracking> {
    console.log('🎯 Analyzing conversion tracking for:', url);

    const systemPrompt = `You are a conversion tracking specialist. Analyze the website for:

1. TRACKING CODES (extract exact IDs):
   - Google Tag Manager: GTM-XXXXXXX
   - Google Analytics 4: G-XXXXXXXXXX
   - Google Ads Conversion: AW-XXXXXXXXX/XXXXXXX
   - Facebook Pixel: numeric ID

2. CONVERSION EVENTS:
   - Event names (purchase, lead, signup, add_to_cart, etc.)
   - Page URLs where conversions happen
   - Elements that trigger conversions
   - Values and currency if detectable

3. CONVERSION FUNNEL:
   - Typical user journey to conversion
   - Key pages in the funnel

4. RECOMMENDATIONS:
   - Primary conversion to optimize for
   - Best bidding strategy based on tracking setup

Return ONLY valid JSON with NO additional text.`;

    const userPrompt = htmlContent
      ? `URL: ${url}\n\nHTML Content: ${htmlContent.substring(0, 10000)}\n\nAnalyze tracking and conversions.`
      : `URL: ${url}\n\nVisit this URL and analyze tracking and conversions.`;

    try {
      const response = await openaiService.chat([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ], {
        temperature: 0.1, // Very precise for factual extraction
        maxTokens: 2000,
        model: 'gpt-4o'
      });

      const data = JSON.parse(response);

      // Structure the response
      const tracking: ConversionTracking = {
        platform: this.determinePlatform(data.trackingIds || {}),
        trackingIds: data.trackingIds || {},
        events: (data.events || []).filter((e: any) => e.confidence >= 70),
        existingConversions: data.existingConversions || [],
        conversionFunnel: data.conversionFunnel || [],
        primaryConversion: data.primaryConversion,
        recommendedBidding: data.recommendedBidding || 'maximize_conversions'
      };

      console.log(`✅ Found ${tracking.events.length} high-confidence conversions`);
      console.log(`📊 Platform: ${tracking.platform}`);

      return tracking;
    } catch (error) {
      console.error('Error analyzing conversion tracking:', error);
      
      // Return empty tracking
      return {
        platform: 'None',
        trackingIds: {},
        events: [],
        existingConversions: [],
        conversionFunnel: [],
        recommendedBidding: 'maximize_conversions'
      };
    }
  },

  /**
   * Determine primary tracking platform
   */
  determinePlatform(trackingIds: any): ConversionTracking['platform'] {
    const hasGTM = !!trackingIds.gtm;
    const hasGA4 = !!trackingIds.ga4;
    const hasGoogleAds = !!trackingIds.googleAds;
    const hasFB = !!trackingIds.facebookPixel;

    const count = [hasGTM, hasGA4, hasGoogleAds, hasFB].filter(Boolean).length;

    if (count > 1) return 'Multiple';
    if (hasGTM) return 'GTM';
    if (hasGA4) return 'GA4';
    if (hasGoogleAds) return 'Google Ads';
    if (hasFB) return 'Facebook';
    return 'None';
  },

  /**
   * Map GA4/GTM events to Google Ads conversion actions
   */
  mapToGoogleAdsConversions(tracking: ConversionTracking): any[] {
    const eventMapping: Record<string, string> = {
      'purchase': 'PURCHASE',
      'add_to_cart': 'ADD_TO_CART',
      'begin_checkout': 'BEGIN_CHECKOUT',
      'generate_lead': 'SUBMIT_LEAD_FORM',
      'contact': 'CONTACT',
      'sign_up': 'SIGN_UP',
      'subscribe': 'SUBSCRIBE'
    };

    return tracking.events.map(event => ({
      name: event.name,
      category: eventMapping[event.name.toLowerCase()] || 'OTHER',
      value: event.value,
      pageUrl: event.pageUrl,
      isPrimary: event.name === tracking.primaryConversion
    }));
  }
};

